import type { AppRouter } from '@bnb/api/router';
import { createTRPCClient, httpBatchLink, type TRPCClient, TRPCClientError } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';

/**
 * Typed API client. Types come straight from the API router (type-only import — no API code
 * ships to the browser), so renaming a procedure or field breaks the web typecheck, not prod.
 *
 * In components:   const trpc = useTRPC();  useQuery(trpc.barns.list.queryOptions())
 * Outside React:   services.trpcClient.barnLog.add.mutate(input)   (e.g. the offline outbox)
 */
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export type ApiClient = TRPCClient<AppRouter>;

/** Same-origin in every stage: CloudFront routes /api/* to the API, Vite proxies it locally. */
export const TRPC_URL = '/api/trpc';

export function createApiClient(getHeaders: () => Promise<Record<string, string>>): ApiClient {
  return createTRPCClient<AppRouter>({
    links: [httpBatchLink({ url: TRPC_URL, headers: getHeaders })],
  });
}

/** tRPC error code (UNAUTHORIZED, FORBIDDEN, BAD_REQUEST, PRECONDITION_FAILED…) or null. */
export function trpcErrorCode(err: unknown): string | null {
  if (err instanceof TRPCClientError) {
    const data: unknown = err.data;
    if (data && typeof data === 'object' && 'code' in data && typeof data.code === 'string') {
      return data.code;
    }
  }
  return null;
}

/** The API refuses writes from builds older than MIN_CLIENT_BUILD with this code. */
export const STALE_CLIENT_CODE = 'PRECONDITION_FAILED';
